// Оформление бейджей — эталонная дизайн-система (Figma, фрейм «бейджи»).
// Цвета заданы макетом явно, поэтому цветовой математики из @teach/ui здесь
// больше нет: выводить оттенки не из чего и не нужно.
import type { CSSProperties } from 'react'
import { blockTheme } from '@teach/ui'
import type { BadgeVariant } from './badgeVariants'

const db = blockTheme.designBlock

/** Размер пилюли ВЫЧИСЛЯЕТСЯ по количеству бейджей (`badgeSizeOf`) — поле
 *  `element.badgeSize` рендер не читает вовсе. Имена значений — рефовские. */
export type AutoBadgeSize = 'large' | 'xlarge'

/** Размер авто: много бейджей (>5) — маленькие, мало (1–5) — большие (ref:627). */
export const badgeSizeOf = (count: number): AutoBadgeSize => (count > 5 ? 'large' : 'xlarge')

/** large = маленький, xlarge = большой (именование рефа). Размеры — как в мобилке
 *  (`design-block/badge-styles.ts`), крупная сверена с макетом Figma («Group 32»,
 *  ÷4.24): текст 14, высота ~31.4, поле 16, от иконки до текста 8. Кегль —
 *  `text-[14px]`, а не `text-sm`: тот несёт свой line-height 20, и пилюля выходила
 *  выше макета; здесь строка — унаследованный `normal` (1.26) канваса. */
export const badgeSizeClass = (s: AutoBadgeSize): string =>
  ({ large: 'px-4 py-1.5 text-[14px] gap-2', xlarge: 'px-4 py-[7px] text-[14px] gap-2' })[s]

/** Иконка 20 у обоих размеров (lucide 20 со штрихом 2 → линия ~1.65 в макете). */
export const badgeIconSizeClass = (s: AutoBadgeSize): string =>
  ({ large: 'w-5 h-5', xlarge: 'w-5 h-5' })[s]

/** Зазор между пилюлями и между рядами — 9.4 (40 px макета ÷ 4.24). Сверху и
 *  снизу ряд добирает общий зазор элементов (4, `space-y-1` канваса) до того же
 *  9.4: чипы-соседи из разных элементов стоят так же, как ряды внутри одного. */
export const BADGE_ROW_CLASS = 'flex flex-wrap justify-center gap-[9.4px] py-[2.7px]'

/** Маппинг сохраняет СМЫСЛ вариантов рефа: `square` был «Заливка», `contrast` —
 *  «Контраст» (прозрачный + рамка), `pastel` был «Стекло» (градиент + тень).
 *  Поэтому ни один существующий курс не меняет визуальное намерение. */
const VARIANT_STYLE: Record<BadgeVariant, CSSProperties> = {
  square: { background: db.badgeFill, color: db.badgeInk },
  // Рамка 1.65 — 7 px макета ÷ 4.24.
  contrast: {
    background: 'transparent',
    color: db.badgeInk,
    border: `1.65px solid ${db.badgeInk}`,
  },
  pastel: {
    background: db.badgeRaised,
    color: db.badgeInk,
    boxShadow: `${db.badgeRaisedShadow}, inset ${db.badgeRaisedShadow}`,
  },
}

/** Фолбэк обязателен: `badgeVariant` из данных бека может оказаться значением,
 *  которого нет в нашем юнионе, и без него пилюля отрисовалась бы без стилей. */
export const badgeVariantStyle = (v: BadgeVariant): CSSProperties =>
  VARIANT_STYLE[v] ?? VARIANT_STYLE.square
